import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { Shell } from "../components/Shell";
import { Ornament } from "../components/Ornament";
import type {
  RegularCreate,
  RegularPublic,
  RegularUpdate,
} from "../lib/types";

export function RegularsPage() {
  const qc = useQueryClient();

  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState("");
  const [defaultQuantity, setDefaultQuantity] = useState(1);
  const [notes, setNotes] = useState("");

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editQuantity, setEditQuantity] = useState(1);
  const [editNotes, setEditNotes] = useState("");

  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery<RegularPublic[]>({
    queryKey: ["regulars"],
    queryFn: () => api.get<RegularPublic[]>("/api/regulars"),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["regulars"] });

  const create = useMutation({
    mutationFn: (payload: RegularCreate) =>
      api.post<RegularPublic>("/api/regulars", payload),
    onSuccess: () => {
      invalidate();
      setName("");
      setDefaultQuantity(1);
      setNotes("");
      setShowAdd(false);
      setError(null);
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError) setError(e.detail);
      else setError("Failed to add this regular.");
    },
  });

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: RegularUpdate }) =>
      api.patch<RegularPublic>(`/api/regulars/${id}`, body),
    onSuccess: () => {
      invalidate();
      setEditingId(null);
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError) setError(e.detail);
      else setError("Failed to save.");
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/regulars/${id}`),
    onSuccess: () => invalidate(),
  });

  const onCreate = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Give it a name before adding.");
      return;
    }
    create.mutate({
      name: trimmed,
      default_quantity: defaultQuantity,
      notes: notes.trim() || null,
    });
  };

  const startEdit = (r: RegularPublic) => {
    setEditingId(r.id);
    setEditName(r.name);
    setEditQuantity(r.default_quantity);
    setEditNotes(r.notes ?? "");
  };

  const saveEdit = (id: number) => {
    const trimmed = editName.trim();
    if (!trimmed) {
      setError("Name cannot be empty.");
      return;
    }
    update.mutate({
      id,
      body: {
        name: trimmed,
        default_quantity: editQuantity,
        notes: editNotes.trim() || null,
      },
    });
  };

  const total = data?.length ?? 0;
  const activeCount = data?.filter((r) => r.active).length ?? 0;

  return (
    <Shell>
      <section className="mb-20 grid grid-cols-12 gap-8 border-b border-ink/80 pb-14">
        <div
          className="col-span-12 md:col-span-3 animate-fade-up"
          style={{ animationDelay: "60ms" }}
        >
          <div className="label mb-3 text-ink-walnut">Section</div>
          <div className="label text-sienna">N° 04</div>
          <div className="label mt-1 text-ink-walnut">
            {activeCount} active {total !== activeCount && `· ${total} total`}
          </div>
        </div>
        <div
          className="col-span-12 md:col-span-9 animate-fade-up"
          style={{ animationDelay: "140ms" }}
        >
          <h1 className="display-1">
            The <span className="display-italic">Regulars</span>
          </h1>
          <div className="mt-8 flex flex-wrap items-end justify-between gap-6">
            <p className="max-w-xl font-body text-xl italic leading-snug text-ink-walnut">
              The pantry things you always need — milk, bread, butter, the
              ordinary staples that find their way onto every list.
            </p>
            <button
              type="button"
              onClick={() => setShowAdd((v) => !v)}
              className="btn-ink shrink-0"
            >
              {showAdd ? "Close" : "+ Add a regular"}
            </button>
          </div>
        </div>
      </section>

      {showAdd && (
        <section
          className="mb-16 border border-ink/30 bg-parchment-deep/60 p-8 animate-fade-up md:p-10"
          style={{ animationDelay: "60ms" }}
        >
          <div className="mb-6 flex items-center gap-4">
            <Ornament className="h-3 w-14 text-sienna" />
            <div className="label text-ink-walnut">A new entry</div>
            <span className="hairline-ink flex-1" />
          </div>
          <form
            onSubmit={onCreate}
            className="grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-[1.4fr_auto_1fr_auto]"
          >
            <div>
              <label className="label mb-2 block text-ink-walnut">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Semi-skimmed milk"
                className="field-input"
                required
                autoFocus
              />
            </div>
            <div>
              <label className="label mb-2 block text-ink-walnut">
                Quantity
              </label>
              <input
                type="number"
                min={1}
                max={99}
                value={defaultQuantity}
                onChange={(e) =>
                  setDefaultQuantity(parseInt(e.target.value || "1", 10))
                }
                className="w-20 border-0 border-b border-ink/30 bg-transparent py-3 text-center font-display text-3xl font-light text-ink focus:border-ink focus:outline-none"
              />
            </div>
            <div>
              <label className="label mb-2 block text-ink-walnut">
                Notes <span className="text-ink-muted">(optional)</span>
              </label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. the blue-top one"
                className="field-input"
              />
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                className="btn-ink"
                disabled={create.isPending}
              >
                {create.isPending ? "Writing…" : "Add"}
              </button>
            </div>
          </form>
        </section>
      )}

      {error && (
        <div className="mb-10 border-l-2 border-sienna pl-4 font-body italic text-sienna animate-fade-in">
          {error}
        </div>
      )}

      {isLoading && (
        <p className="label animate-fade-in text-ink-muted">
          Gathering the pantry…
        </p>
      )}

      {isError && (
        <div className="border-l-2 border-sienna pl-4 font-body italic text-sienna">
          The pantry could not be read.
        </div>
      )}

      {data && data.length === 0 && (
        <div
          className="py-24 text-center animate-fade-up"
          style={{ animationDelay: "200ms" }}
        >
          <Ornament
            className="mx-auto mb-10 h-5 w-28 text-sienna"
            variant="default"
          />
          <h3 className="display-2">
            Nothing <span className="display-italic">regular</span> yet.
          </h3>
          <p className="mx-auto mt-6 max-w-md font-body text-lg italic text-ink-walnut">
            Add the things you always reach for — they'll appear on every
            weekly list.
          </p>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="btn-ink mt-12 inline-flex"
          >
            Add the first one
          </button>
        </div>
      )}

      {data && data.length > 0 && (
        <ol className="divide-y divide-ink/15">
          {data.map((r, i) => {
            const isEditing = editingId === r.id;
            return (
              <li
                key={r.id}
                className={`animate-fade-up ${
                  r.active ? "" : "opacity-50"
                }`}
                style={{ animationDelay: `${220 + i * 40}ms` }}
              >
                {!isEditing && (
                  <div className="group grid grid-cols-12 items-baseline gap-x-6 gap-y-3 py-8">
                    <div className="label col-span-3 text-ink-walnut md:col-span-1">
                      N° {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="col-span-9 md:col-span-6">
                      <h3
                        className={`font-display text-3xl font-light leading-tight tracking-[-0.01em] text-ink md:text-4xl ${
                          r.active ? "" : "line-through decoration-ink/40"
                        }`}
                      >
                        {r.name}
                      </h3>
                      {r.notes && (
                        <p className="mt-2 font-body text-base italic text-ink-walnut">
                          {r.notes}
                        </p>
                      )}
                    </div>
                    <div className="label col-span-6 text-ink-walnut md:col-span-2 md:text-right">
                      × {r.default_quantity}
                    </div>
                    <div className="col-span-6 flex items-center justify-end gap-5 md:col-span-3">
                      <button
                        type="button"
                        onClick={() =>
                          update.mutate({
                            id: r.id,
                            body: { active: !r.active },
                          })
                        }
                        className="label ink-link text-ink-walnut"
                        aria-label={
                          r.active ? "Retire this regular" : "Reactivate"
                        }
                      >
                        {r.active ? "Retire" : "Reactivate"}
                      </button>
                      <button
                        type="button"
                        onClick={() => startEdit(r)}
                        className="label ink-link text-ink-walnut"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (
                            confirm(
                              `Remove “${r.name}” from the pantry? This cannot be undone.`,
                            )
                          ) {
                            remove.mutate(r.id);
                          }
                        }}
                        className="label text-ink-walnut transition-colors hover:text-sienna"
                        aria-label={`Remove ${r.name}`}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}

                {isEditing && (
                  <div className="grid grid-cols-12 items-end gap-x-6 gap-y-4 py-8">
                    <div className="label col-span-3 text-sienna md:col-span-1">
                      N° {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="col-span-12 md:col-span-5">
                      <label className="label mb-2 block text-ink-walnut">
                        Name
                      </label>
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="field-input"
                      />
                    </div>
                    <div className="col-span-6 md:col-span-2">
                      <label className="label mb-2 block text-ink-walnut">
                        Quantity
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={99}
                        value={editQuantity}
                        onChange={(e) =>
                          setEditQuantity(
                            parseInt(e.target.value || "1", 10),
                          )
                        }
                        className="w-20 border-0 border-b border-ink/30 bg-transparent py-2 text-center font-display text-2xl font-light text-ink focus:border-ink focus:outline-none"
                      />
                    </div>
                    <div className="col-span-12 md:col-span-4">
                      <label className="label mb-2 block text-ink-walnut">
                        Notes
                      </label>
                      <input
                        type="text"
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        className="field-input"
                      />
                    </div>
                    <div className="col-span-12 flex items-center gap-6 md:col-start-2 md:col-end-13">
                      <button
                        type="button"
                        onClick={() => saveEdit(r.id)}
                        className="btn-ink"
                        disabled={update.isPending}
                      >
                        {update.isPending ? "Saving…" : "Save"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingId(null)}
                        className="label ink-link text-ink-walnut"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}

      <div className="mt-28 flex items-center gap-6">
        <span className="hairline flex-1" />
        <Ornament className="h-4 w-20 text-sienna" />
        <span className="hairline flex-1" />
      </div>
    </Shell>
  );
}
